/**
 * The task form's vocabulary and its two mappings — form ⇄ API — shared by
 * the create and edit pages so a field added here works on both (the
 * 2026-08-30 mapping found that a field added to the config alone worked on
 * create and was silently wiped on every edit).
 *
 * Contract: docs/TASK_FORM_FIELD_CONTRACT_2026-09-04.md.
 */
import type { CreateTaskRequest, Task, TaskScore, TaskStatus, UpdateTaskRequest } from '../services/taskService'
import { daysUntil } from './taskSections'
import { formatDuration } from './dayShape'

// The form holds every field kind the shared form renders, so its values are
// genuinely dynamic; the two mapping functions below are where they get typed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TaskFormData = Record<string, any>
/** What the form sends: a create body that is also a valid update body. */
export type TaskPayload = CreateTaskRequest & UpdateTaskRequest & { hold_lead_minutes?: number | null; hold_interval_minutes?: number | null }

export interface Choice { value: string; label: string }

/** "When" is one chip row on the form; it writes three fields (§1, Tier B). */
export const WHEN_OPTIONS: Choice[] = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'date', label: 'Pick a date' },
  { value: 'someday', label: 'Someday' },
]
export const DURATION_OPTIONS: Choice[] = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hour' },
  { value: '120', label: '2 hours' },
  { value: '240', label: 'Half a day' },
]
export const ENERGY_OPTIONS: Choice[] = [
  { value: 'low', label: 'Autopilot' },
  { value: 'medium', label: 'Steady' },
  { value: 'high', label: 'Sharp' },
]
export const CONTEXT_OPTIONS: Choice[] = [
  { value: 'anywhere', label: 'Anywhere' },
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
  { value: 'out', label: 'Out' },
]
/** Mirrors shared_utilities/services/life_area.LIFE_AREAS minus the placeholder. */
export const LIFE_AREA_OPTIONS: Choice[] = [
  { value: 'health', label: 'Health' },
  { value: 'career', label: 'Career' },
  { value: 'relationships', label: 'Relationships' },
  { value: 'money', label: 'Money' },
  { value: 'learning', label: 'Learning' },
  { value: 'personal_growth', label: 'Personal growth' },
  { value: 'productivity', label: 'Getting organised' },
]
/** How much it matters — the level prioritisation already weights; the text is the why. */
export const MATTERS_OPTIONS: Choice[] = [
  { value: 'low', label: 'A little' },
  { value: 'medium', label: 'Some' },
  { value: 'high', label: 'A lot' },
]
export const DEADLINE_TYPE_OPTIONS: Choice[] = [
  { value: 'soft', label: 'There is some give' },
  { value: 'hard', label: 'There is a real cutoff' },
]
/** Values unchanged (every engine reads them); only the words are new (§2). */
export const PRIORITY_OPTIONS: Choice[] = [
  { value: 'low', label: 'Whenever' },
  { value: 'medium', label: 'Normal' },
  { value: 'high', label: 'Important' },
  { value: 'urgent', label: 'Urgent' },
]
export const STATUS_OPTIONS: Choice[] = [
  { value: 'pending', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'on_hold', label: 'Paused' },
  { value: 'completed', label: 'Done' },
  { value: 'cancelled', label: 'Dropped' },
  { value: 'lapsed', label: 'Let go' },
  { value: 'parked', label: 'Parked' },
]
/** Every status a task can carry has words. The engines file tasks as `active`
 *  (prod 2026-09-12: all open tasks) — the same "not started" as `pending`; the
 *  pages used to print the raw word and hide Start. */
export const STATUS_LABEL: Record<string, string> = { ...Object.fromEntries(STATUS_OPTIONS.map(o => [o.value, o.label])), active: 'Not started' }
export const PRIORITY_LABEL: Record<string, string> = Object.fromEntries(PRIORITY_OPTIONS.map(o => [o.value, o.label]))
export const LIFE_AREA_LABEL: Record<string, string> = Object.fromEntries(LIFE_AREA_OPTIONS.map(o => [o.value, o.label]))
/** A life area as the server stores it: lower-case, words joined by underscores,
 *  at most 40 characters — "Side project" and "side_project" are one area. */
export function lifeAreaValue(text: string): string {
  return String(text || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 40)
}
/** Words for a life area: the canonical label, or the person's own name tidied. */
export function lifeAreaLabel(value: string): string {
  if (LIFE_AREA_LABEL[value]) return LIFE_AREA_LABEL[value]
  const words = String(value || '').replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : ''
}

/** Words that mean the title names a thing, not an action. Same list as
 *  task_score.py so the form's hint and the score agree. */
const NOT_A_VERB = new Set([
  'the', 'a', 'an', 'my', 'our', 'this', 'that', 'these', 'those', 'some', 'new', 'more', 'i', 'we', 'it',
  'its', 'for', 'of', 'to', 'and', 'or', 'with', 'about', 're', 'fw', 'fwd', 'idea', 'ideas', 'note', 'notes',
  'todo', 'task', 'stuff',
])

/** True when a title reads as something to do. A hint on the form, never a block. */
export function verbFirst(title: string): boolean {
  const words = (title || '').trim().split(/\s+/).filter(Boolean)
  if (words.length < 3) return false
  const first = words[0]!.toLowerCase().replace(/^["'([]+/, '').replace(/[:.,;-]+$/, '')
  return !NOT_A_VERB.has(first)
}

/** The soft nudge under the title. Empty when there is nothing to say yet. */
export function titleHint(title: string): string | null {
  const t = (title || '').trim()
  if (t.split(/\s+/).filter(Boolean).length < 2) return null
  return verbFirst(t) ? null : 'Try starting with a verb — Call, Write, Book…'
}

/** The caption under the ring: a count, never a grade. */
export function missingCaption(score: TaskScore | undefined | null): string | null {
  if (!score || !score.scored) return null
  const n = score.missing.length
  if (n === 0) return 'ready to plan'
  return n === 1 ? '1 detail missing' : `${n} details missing`
}

/** Which "when" chip a stored task is on. */
export function whenOf(task: Partial<Task>): string {
  if (task.due_date) return 'date'
  const tw = task.target_window || ''
  if (tw === 'today' || tw === 'this_week' || tw === 'this_month' || tw === 'someday') return tw
  if (task.user_tier === 'backlog') return 'someday'
  if (task.user_tier === 'committed') return 'today'
  return ''
}

/** Form state for an existing task. Everything the form can show must be here,
 *  or an edit silently wipes it. */
export function taskToFormData(task: Task): TaskFormData {
  const parentType = task.parent_skill_id ? 'skill' : task.parent_goal_id ? 'goal' : ''
  const parentId = task.parent_skill_id ?? task.parent_goal_id ?? null
  return {
    title: task.title,
    description: task.description || '',
    priority: task.priority || 'medium',
    status: task.status,
    progress: task.progress,
    user_tier: task.user_tier || '',
    estimated_duration: task.estimated_duration || '',
    when: whenOf(task),
    due_date: task.due_date,
    due_time: task.due_time,
    hold_lead_minutes: task.hold_lead_minutes,
    hold_interval_minutes: task.hold_interval_minutes,
    deadline_type: task.deadline_type,
    deadline_consequence: task.deadline_consequence,
    deadline_risk_level: task.deadline_risk_level,
    parent_type: parentType,
    parent_id: parentId,
    parent_goal_id: parentType === 'goal' ? parentId : undefined,
    parent_skill_id: parentType === 'skill' ? parentId : undefined,
    life_area: task.life_area || '',
    life_areas: task.life_areas && task.life_areas.length ? [...task.life_areas] : (task.life_area ? [task.life_area] : []),
    energy_level_required: task.energy_level_required || '',
    context: task.context || '',
    place_id: task.place_id ?? null,
    acceptance_criteria: task.acceptance_criteria || '',
    minimum_starter_step: task.minimum_starter_step || '',
    motivation: task.motivation || '',
    motivation_level: task.motivation_level || '',
    is_recurring: task.is_recurring || false,
    recurrence_pattern: task.recurrence_pattern,
    recurrence_rule: task.recurrence_rule,
    recurrence_end_date: task.recurrence_end_date,
  }
}

function numOrNull(v: unknown): number | null {
  if (v === '' || v == null) return null
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * What the form sends. `when` becomes target_window / due_date / user_tier;
 * a chosen parent means the server inherits the life area, so an explicit
 * life_area is only sent when there is no parent.
 */
export function taskFormToPayload(form: TaskFormData, mode: 'create' | 'edit'): TaskPayload {
  const parentType = form.parent_type as string
  const parentId = form.parent_id ? Number(form.parent_id) : null
  const when = (form.when as string) || ''
  const hasDate = when === 'date'

  const payload: TaskPayload = {
    title: String(form.title || '').trim(),
    description: form.description || '',
    priority: form.priority || 'medium',
    estimated_duration: numOrNull(form.estimated_duration) ?? undefined,
    parent_goal_id: parentType === 'goal' && parentId ? parentId : null,
    parent_skill_id: parentType === 'skill' && parentId ? parentId : null,
    parent_dream_id: null,
    parent_aspiration_id: null,
    // Life areas (2026-09-05): a list, first = primary; `life_area` mirrors it.
    life_areas: !parentId ? (Array.isArray(form.life_areas) ? form.life_areas.map(String) : (form.life_area ? [String(form.life_area)] : [])) : (mode === 'create' ? undefined : []),
    life_area: !parentId ? ((Array.isArray(form.life_areas) && form.life_areas[0]) ? String(form.life_areas[0]) : (form.life_area ? String(form.life_area) : (mode === 'create' ? undefined : null))) : (mode === 'create' ? undefined : null),
    energy_level_required: form.energy_level_required || null,
    context: form.context || null,
    place_id: form.place_id != null && form.place_id !== '' ? Number(form.place_id) : null,
    acceptance_criteria: form.acceptance_criteria?.trim?.() || null,
    minimum_starter_step: form.minimum_starter_step?.trim?.() || null,
    // The why is a standalone task's own; a task under a goal inherits the goal's.
    motivation: !parentId ? (form.motivation?.trim?.() || null) : undefined,
    motivation_level: !parentId ? (form.motivation_level || null) : undefined,
    is_recurring: Boolean(form.is_recurring),
    recurrence_pattern: form.recurrence_pattern,
    recurrence_rule: form.recurrence_rule,
    recurrence_end_date: form.recurrence_end_date,
    // deadline + hold: only meaningful with a date
    due_date: hasDate ? (form.due_date || undefined) : (mode === 'edit' ? (null as unknown as string) : undefined),
    due_time: hasDate ? (form.due_time || null) : null,
    deadline_type: hasDate ? (form.deadline_type || undefined) : undefined,
    deadline_consequence: hasDate ? (form.deadline_consequence?.trim?.() || undefined) : undefined,
    hold_lead_minutes: hasDate && form.due_time && form.hold_lead_minutes !== '' && form.hold_lead_minutes != null
      ? Number(form.hold_lead_minutes) : null,
    hold_interval_minutes: hasDate && form.due_time && form.hold_interval_minutes !== '' && form.hold_interval_minutes != null
      ? Number(form.hold_interval_minutes) : null,
  }

  // "When" writes three fields. '' on create sends nothing; on edit it leaves
  // the stored answer alone, because an untouched chip is not a decision.
  if (when === 'today') { payload.target_window = 'today'; payload.user_tier = 'committed' }
  else if (when === 'someday') { payload.target_window = 'someday'; payload.user_tier = 'backlog' }
  else if (when === 'this_week' || when === 'this_month') { payload.target_window = when; payload.user_tier = null }
  else if (when === 'date') { payload.target_window = null; payload.user_tier = form.user_tier || null }

  if (mode === 'create') {
    payload.created_via = 'manual'
  } else {
    payload.status = form.status as TaskStatus
    if (form.progress !== undefined && form.progress !== '') payload.progress = Number(form.progress)
  }
  // Strip undefined so the JSON is what we mean.
  const bag = payload as unknown as Record<string, unknown>
  for (const k of Object.keys(bag)) if (bag[k] === undefined) delete bag[k]
  return payload
}

// ── Due line + delete cleanup copy (task review 2026-09-04 §P1-7, audit 2026-09-05) ──

function formatDueDate(iso: string): string {
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatClock(hhmm: string): string {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10))
  if (Number.isNaN(h)) return hhmm
  const d = new Date()
  d.setHours(h, Number.isNaN(m) ? 0 : m, 0, 0)
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function minusMinutes(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10))
  if (Number.isNaN(h)) return hhmm
  const total = ((h * 60 + (Number.isNaN(m) ? 0 : m) - minutes) % 1440 + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** "Due Fri, Sep 12 · 5:00 PM · reminds from 4:00 PM, every 30 min" — one
 *  line for the detail page instead of the bare date. */
export function dueLine(task: {
  due_date?: string | null
  due_time?: string | null
  deadline_type?: string | null
  deadline_timezone?: string | null
  hold_lead_minutes?: number | null
  hold_interval_minutes?: number | null
}): string | null {
  if (!task.due_date) return null
  const parts = [`${task.deadline_type === 'hard' ? 'Complete by' : 'Due'} ${formatDueDate(task.due_date)}`]
  if (task.due_time) parts.push(clockWithZone(task.due_time, task.deadline_timezone))
  if (task.due_time && task.hold_lead_minutes) {
    const from = formatClock(minusMinutes(task.due_time, task.hold_lead_minutes))
    parts.push(task.hold_interval_minutes
      ? `reminds from ${from}, every ${task.hold_interval_minutes} min`
      : `reminds from ${from}`)
  }
  return parts.join(' · ')
}

/** Short name of a zone ("PDT"), the deadline's own when it was snapshotted,
 *  else the browser's. Falls back to the IANA id when Intl has no short name. */
export function zoneAbbrev(tz?: string | null, at: Date = new Date()): string {
  const zone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(at).find((p) => p.type === 'timeZoneName')
    return part?.value || zone
  } catch {
    return zone
  }
}

/** "5:00 PM PDT" — a clock time is a different moment to every reader
 *  (owner rule 2026-09-05: every displayed time carries its zone). */
export function clockWithZone(hhmm: string, tz?: string | null): string {
  return `${formatClock(hhmm)} ${zoneAbbrev(tz)}`
}

const CLEANUP_NOUN: Record<string, [string, string]> = {
  schedule_blocks: ['calendar slot', 'calendar slots'],
  focus_pins: ['Today pin', 'Today pins'],
  todays_focus: ['Today entry', 'Today entries'],
  reminders: ['reminder', 'reminders'],
  breakdown_steps: ['step', 'steps'],
  calendar_series: ['calendar series', 'calendar series'],
  checkins: ['check-in', 'check-ins'],
}

/** Turns the delete cascade counts into "Also removed 2 calendar slots and
 *  1 reminder." — the doctrine wants the surface to say a delete also stops
 *  its reminder. Null when nothing else went. */
export function describeTaskCleanup(cleanup?: Record<string, number> | null): string | null {
  if (!cleanup) return null
  const bits = Object.entries(cleanup)
    .filter(([key, n]) => n > 0 && CLEANUP_NOUN[key])
    .map(([key, n]) => `${n} ${CLEANUP_NOUN[key][n === 1 ? 0 : 1]}`)
  if (bits.length === 0) return null
  const list = bits.length === 1 ? bits[0] : `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}`
  return `Also removed ${list}.`
}

/** "just now" · "12 min ago" · "3 h ago" · "yesterday" · "4 days ago" · else a
 *  short date. For the row's "started …" and the detail's status line. */
export function relativeTime(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso)
  if (Number.isNaN(d.getTime())) return null
  const s = Math.round((Date.now() - d.getTime()) / 1000)
  if (s < 60) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} h ago`
  const days = Math.round(h / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export const WINDOW_LABEL: Record<string, string> = {
  today: 'Today', this_week: 'This week', this_month: 'This month', someday: 'Someday',
}

/** The duration words every task surface uses — "45 min", "1 hr", "1 hr 30 min":
 *  the one contract /today keeps in `taskMeta` (North Star rule 4). The task
 *  pages said "45m" / "1h 30m" for the same fact until 2026-09-12. */
export function durationText(minutes?: number | null): string | null {
  return formatDuration(minutes) || null
}

// ── Words for the planning facts, shared by the detail page and its inline editors ──

type PlanWordsTask = { due_date?: string | null; target_window?: string | null; energy_level_required?: string | null; context?: string | null; deadline_type?: string | null }

export function whenWords(task: PlanWordsTask): string | null {
  if (task.due_date) {
    const d = new Date(task.due_date.length <= 10 ? `${task.due_date}T12:00:00` : task.due_date)
    return `${task.deadline_type === 'hard' ? 'Complete by' : 'Due'} ${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`
  }
  return task.target_window ? (WINDOW_LABEL[task.target_window] || task.target_window) : null
}


export function fitWords(task: PlanWordsTask): string | null {
  const parts = [
    task.energy_level_required ? ({ low: 'Autopilot', medium: 'Steady', high: 'Sharp' } as Record<string, string>)[task.energy_level_required] : null,
    task.context ? task.context.charAt(0).toUpperCase() + task.context.slice(1) : null,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

export function stakesWords(task: { deadline_type?: string | null; deadline_consequence?: string | null }): string | null {
  const parts = [task.deadline_type === 'hard' ? 'A real cutoff' : task.deadline_type === 'soft' ? 'Some give' : null, task.deadline_consequence].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`
}

/** ONE line for how a task repeats — "Repeats weekly on Mon, Wed, Fri · until
 *  Sat, Sep 19". Reads the rule the picker writes and the server expands
 *  (`days_of_week` 0 = Sunday). Until 2026-09-12 the page said it three ways
 *  ("Repeats · weekly", then the rule, then the end date) and named the days
 *  Monday-first, so a Mon/Wed/Fri rule read "Tue, Thu, Sat" above a strip that
 *  drew Mondays. Null when the task does not repeat. */
export function recurrenceWords(task: { is_recurring?: boolean; recurrence_pattern?: string | null; recurrence_rule?: unknown; recurrence_end_date?: string | null }): string | null {
  if (!task.is_recurring) return null
  let rule: Record<string, unknown> = {}
  try {
    rule = typeof task.recurrence_rule === 'string' ? JSON.parse(task.recurrence_rule) : ((task.recurrence_rule as Record<string, unknown>) || {})
  } catch { rule = {} }
  const freq = String(rule.frequency || rule.type || task.recurrence_pattern || '').toLowerCase()
  const interval = Number(rule.interval) > 1 ? Number(rule.interval) : 1
  const days = (Array.isArray(rule.days_of_week) ? rule.days_of_week : []).map(Number).filter((d) => d >= 0 && d <= 6).map((d) => DAY_SHORT[d])
  let head: string
  switch (freq) {
    case 'daily': head = interval > 1 ? `every ${interval} days` : 'daily'; break
    case 'weekdays': head = 'on weekdays'; break
    case 'weekly': head = interval > 1 ? `every ${interval} weeks` : 'weekly'; break
    case 'biweekly': head = 'every 2 weeks'; break
    case 'monthly': head = `${interval > 1 ? `every ${interval} months` : 'monthly'}${rule.day_of_month ? ` on the ${ordinal(Number(rule.day_of_month))}` : ''}`; break
    case '': head = ''; break
    default: head = freq.replace(/_/g, ' ')
  }
  const onDays = days.length && freq !== 'daily' && freq !== 'weekdays' ? ` on ${days.join(', ')}` : ''
  const parts = [`Repeats${head ? ` ${head}` : ''}${onDays}`]
  const end = (rule.end_date as string | undefined) || task.recurrence_end_date
  const times = Number(rule.occurrences || rule.count)
  if (end) parts.push(`until ${shortDate(String(end))}`)
  else if (times > 0) parts.push(`${times} times`)
  return parts.join(' · ')
}


function shortDate(dateStr: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr)
  if (!m) return dateStr
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

/** The list row's "when" words for an open task, with a tone. Soft even when
 *  late ("Past its time", never overdue); a clock time always names its zone.
 *  `planned` is false only when nothing at all has been said about when. */
export function whenMeta(task: Task, today: Date): { text: string; tone: string; planned: boolean } {
  if (task.due_date) {
    const days = daysUntil(task.due_date, today)
    const clock = task.due_time ? ` · ${clockWithZone(task.due_time, task.deadline_timezone)}` : ''
    const label = task.deadline_type === 'hard' ? 'Complete by' : 'Due'
    if (days != null && days < 0) return { text: `Past its time · ${shortDate(task.due_date)}${clock}`, tone: 'text-warn font-medium', planned: true }
    if (days === 0) return { text: `${label} today${clock}`, tone: 'text-warn font-medium', planned: true }
    if (days === 1) return { text: `${label} tomorrow${clock}`, tone: 'text-warn', planned: true }
    return { text: `${label} ${shortDate(task.due_date)}${clock}`, tone: 'text-secondary', planned: true }
  }
  if ((task.status as string) === 'parked') return { text: 'Parked', tone: 'text-muted', planned: true }
  if (task.status === 'on_hold') return { text: 'Paused', tone: 'text-muted', planned: true }
  if (task.user_tier === 'backlog') return { text: 'Backlog', tone: 'text-muted', planned: true }
  if (task.target_window) return { text: WINDOW_LABEL[task.target_window] || task.target_window, tone: 'text-secondary', planned: true }
  return { text: 'No time set', tone: 'text-muted', planned: false }
}
